import React, { useCallback, useContext, useState } from 'react';
import { Button, Col, message, Modal, Row } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { ButtonActionPage, SpinLoading, SelectOption } from '../../atoms';
import { ConfirmAction } from '../../molecules/confirm/ConfirmAction';
import { postTranslationSync } from '../../../repository/mytm/translation/translationRepository';
import { UserContext } from '../../../repository/context/userContext';
import { postLoginRepository } from '../../../repository/mytm/auth/loginRepository';
import { MESSAGE_ERROR } from '../../../constants/message';
import { GRAPHICS } from '../../../constants/define/translation';
import { LANGUAGES } from '../../../constants/graphics';
import { Language } from '../../../types/graphics';
import { appStyles } from '../../../styles';

export const TextPageContent = () => {
  const { t } = useTranslation(GRAPHICS);
  const userContext = useContext(UserContext);

  const [language, setLanguage] = useState<Language>(Language.ENGLISH);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [linkGoogleSheet, setLinkGoogleSheet] = useState(process.env.REACT_APP_GOOGLE_SHEET_LOCALIZATION || '#');

  const onHandleSelectLanguage = useCallback((value: Language) => {
    setLanguage(value);
    if (value === Language.ALL || value === Language.ENGLISH) {
      setLinkGoogleSheet(process.env.REACT_APP_GOOGLE_SHEET_LOCALIZATION || '#');
    } else if (value === Language.JAPANESE) {
      setLinkGoogleSheet(process.env.REACT_APP_GOOGLE_SHEET_LOCALIZATION_JP || '#');
    } else {
      setLinkGoogleSheet('#');
    }
  }, []);

  const onClickButtonSync = useCallback(() => {
    Modal.confirm({
      title: t('popup.title'),
      okText: t('popup.okBtn'),
      cancelText: t('popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={
            <>
              <span>{t('popup.syncTransition')}</span>
            </>
          }
          isConfirmPassword
          textConfirmPassword={t('popup.confirmPassword')}
        />
      ),
      onOk() {
        setIsLoading(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setIsLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              const onlyLanguage = language !== Language?.ALL ? language : undefined;
              postTranslationSync({ onlyLanguage })
                .then(r => {
                  setIsLoading(false);
                  if (r.status === 200) {
                    if (onlyLanguage) {
                      if (r.data?.results[onlyLanguage.toLowerCase()]?.success) {
                        message.success(t('message.syncTransitionSuccess'));
                      } else {
                        message.error(t('message.syncTransitionFailure'));
                      }
                    } else {
                      let errorSheet: any;
                      for (const i of Object.keys(r.data?.results)) {
                        if (!r.data?.results[i.toLocaleLowerCase()].success) {
                          errorSheet = i;
                          break;
                        }
                      }
                      if (errorSheet) {
                        message.error(`${t('message.syncTransitionFailure')} ${errorSheet} sheet`);
                      } else {
                        message.success(t('message.syncTransitionSuccess'));
                      }
                    }
                  }
                })
                .catch(e => {
                  setIsLoading(false);
                  message.error(t('message.syncTransitionFailure'));
                });
            })
            .catch((e: any) => {
              setIsLoading(false);
              message.error(e?.errorMessage);
            });
        }
      },
    });
  }, [language]);

  return (
    <SpinLoading isLoading={isLoading} absolute>
      <Container>
        <Row>
          <Col span={6}>
            <div className="text-filter">
              <p className="text">{t('filter.language')}: </p>
            </div>
            <SelectOption
              values={LANGUAGES}
              showSearch
              defaultValue={Language.ENGLISH}
              onChange={onHandleSelectLanguage}
              textDisplay="text"
            />
          </Col>
          <Col span={6} className="view-detail">
            <a href={linkGoogleSheet} target={linkGoogleSheet !== '#' ? '_blank' : '_self'} rel="noreferrer">
              <Button type="primary" size="middle">
                {t('buttonViewSheet')}
              </Button>
            </a>
          </Col>
        </Row>
        <Row style={{ justifyContent: 'flex-begin', paddingTop: 20 }}>
          <ButtonActionPage
            text={t('buttonSync')}
            id="1"
            className="btn-translation-sync"
            onClick={onClickButtonSync}
          />
        </Row>
      </Container>
    </SpinLoading>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}
  .ant-select {
    width: 100%;
  }
  & .view-detail {
    padding-top: 36px;
    padding-left: 20px;
    & button {
      ${appStyles.commonBorderRadius}
    }
  }
`;
